"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.suggestDepositWallet = exports.getRevealedDeposit = exports.revealDeposit = exports.calculateDepositAddress = exports.calculateDepositScriptHash = exports.calculateDepositRefundLocktime = exports.validateDepositScriptParameters = exports.assembleDepositScript = exports.assembleDepositTransaction = exports.submitDepositTransaction = void 0;
const bcoin_1 = __importDefault(require("bcoin"));
const ethers_1 = require("ethers");
const bitcoin_1 = require("./bitcoin");
const bitcoin_network_1 = require("./bitcoin-network");
const hex_1 = require("./hex");
const { opcodes } = bcoin_1.default.script.common;
/**
 * Submits a deposit by creating and broadcasting a Bitcoin P2(W)SH
 * deposit transaction.
 * @param deposit - Details of the deposit.
 * @param depositorPrivateKey - Bitcoin private key of the depositor.
 * @param bitcoinClient - Bitcoin client used to interact with the network.
 * @param witness - If true, a witness (P2WSH) transaction will be created.
 *        Otherwise, a legacy P2SH transaction will be made.
 * @returns The outcome consisting of:
 *          - the deposit transaction hash,
 *          - the deposit UTXO produced by this transaction.
 */
async function submitDepositTransaction(deposit, depositorPrivateKey, bitcoinClient, witness) {
    const depositorKeyRing = (0, bitcoin_1.createKeyRing)(depositorPrivateKey);
    const depositorAddress = depositorKeyRing.getAddress("string");
    const utxos = await bitcoinClient.findAllUnspentTransactionOutputs(depositorAddress);
    const utxosWithRaw = [];
    for (const utxo of utxos) {
        const utxoRawTransaction = await bitcoinClient.getRawTransaction(utxo.transactionHash);
        utxosWithRaw.push(Object.assign(Object.assign({}, utxo), { transactionHex: utxoRawTransaction.transactionHex }));
    }
    const { transactionHash, depositUtxo, rawTransaction } = await assembleDepositTransaction(deposit, utxosWithRaw, depositorPrivateKey, witness);
    await bitcoinClient.broadcast(rawTransaction);
    return {
        transactionHash,
        depositUtxo,
    };
}
exports.submitDepositTransaction = submitDepositTransaction;
/**
 * Assembles a Bitcoin P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @param utxos - UTXOs that should be used as transaction inputs.
 * @param depositorPrivateKey - Bitcoin private key of the depositor.
 * @param witness - If true, a witness (P2WSH) transaction will be created.
 *        Otherwise, a legacy P2SH transaction will be made.
 * @returns The outcome consisting of:
 *          - the deposit transaction hash,
 *          - the deposit UTXO produced by this transaction.
 *          - the deposit transaction in the raw format
 */
async function assembleDepositTransaction(deposit, utxos, depositorPrivateKey, witness) {
    const depositorKeyRing = (0, bitcoin_1.createKeyRing)(depositorPrivateKey);
    const depositorAddress = depositorKeyRing.getAddress("string");
    const inputCoins = utxos.map((utxo) => bcoin_1.default.Coin.fromTX(bcoin_1.default.MTX.fromRaw(utxo.transactionHex, "hex"), utxo.outputIndex, -1));
    const transaction = new bcoin_1.default.MTX();
    const scriptHash = await calculateDepositScriptHash(deposit, witness);
    transaction.addOutput({
        script: witness
            ? bcoin_1.default.Script.fromProgram(0, scriptHash)
            : bcoin_1.default.Script.fromScripthash(scriptHash),
        value: deposit.amount.toNumber(),
    });
    await transaction.fund(inputCoins, {
        rate: null,
        changeAddress: depositorAddress,
        subtractFee: false, // do not subtract the fee from outputs
    });
    transaction.sign(depositorKeyRing);
    const transactionHash = bitcoin_1.TransactionHash.from(transaction.txid());
    return {
        transactionHash,
        depositUtxo: {
            transactionHash,
            outputIndex: 0,
            value: deposit.amount,
        },
        rawTransaction: {
            transactionHex: transaction.toRaw().toString("hex"),
        },
    };
}
exports.assembleDepositTransaction = assembleDepositTransaction;
/**
 * Assembles a Bitcoin locking script for P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @returns Script as an un-prefixed hex string.
 */
async function assembleDepositScript(deposit) {
    validateDepositScriptParameters(deposit);
    // All HEXes pushed to the script must be un-prefixed.
    const script = new bcoin_1.default.Script();
    script.clear();
    script.pushData(Buffer.from(deposit.depositor.identifierHex, "hex"));
    script.pushOp(opcodes.OP_DROP);
    script.pushData(Buffer.from(deposit.blindingFactor, "hex"));
    script.pushOp(opcodes.OP_DROP);
    script.pushOp(opcodes.OP_DUP);
    script.pushOp(opcodes.OP_HASH160);
    script.pushData(Buffer.from(deposit.walletPublicKeyHash, "hex"));
    script.pushOp(opcodes.OP_EQUAL);
    script.pushOp(opcodes.OP_IF);
    script.pushOp(opcodes.OP_CHECKSIG);
    script.pushOp(opcodes.OP_ELSE);
    script.pushOp(opcodes.OP_DUP);
    script.pushOp(opcodes.OP_HASH160);
    script.pushData(Buffer.from(deposit.refundPublicKeyHash, "hex"));
    script.pushOp(opcodes.OP_EQUALVERIFY);
    script.pushData(Buffer.from(deposit.refundLocktime, "hex"));
    script.pushOp(opcodes.OP_CHECKLOCKTIMEVERIFY);
    script.pushOp(opcodes.OP_DROP);
    script.pushOp(opcodes.OP_CHECKSIG);
    script.pushOp(opcodes.OP_ENDIF);
    script.compile();
    // Return script as HEX string.
    return script.toRaw().toString("hex");
}
exports.assembleDepositScript = assembleDepositScript;
// eslint-disable-next-line valid-jsdoc
/**
 * Validates the given deposit script parameters. Throws in case of a
 * validation error.
 * @param deposit - The validated deposit script parameters.
 * @dev This function does not validate the depositor's identifier as its
 *      validity is chain-specific. This parameter must be validated outside.
 */
function validateDepositScriptParameters(deposit) {
    if (deposit.blindingFactor.length != 16) {
        throw new Error("Blinding factor must be an 8-byte number");
    }
    if (!(0, bitcoin_1.isPublicKeyHashLength)(deposit.walletPublicKeyHash)) {
        throw new Error("Invalid wallet public key hash");
    }
    if (!(0, bitcoin_1.isPublicKeyHashLength)(deposit.refundPublicKeyHash)) {
        throw new Error("Invalid refund public key hash");
    }
    if (deposit.refundLocktime.length != 8) {
        throw new Error("Refund locktime must be a 4-byte number");
    }
}
exports.validateDepositScriptParameters = validateDepositScriptParameters;
/**
 * Calculates a refund locktime parameter for the given deposit creation timestamp.
 * Throws if the resulting locktime is not a 4-byte number.
 * @param depositCreatedAt - Unix timestamp in seconds determining the moment
 *        of deposit creation.
 * @param depositRefundLocktimeDuration - Deposit refund locktime duration in seconds.
 * @returns A 4-byte little-endian deposit refund locktime as an un-prefixed
 *          hex string.
 */
function calculateDepositRefundLocktime(depositCreatedAt, depositRefundLocktimeDuration) {
    // Locktime is a Unix timestamp in seconds, computed as deposit creation
    // timestamp plus locktime duration.
    const locktime = ethers_1.BigNumber.from(depositCreatedAt + depositRefundLocktimeDuration);
    const locktimeHex = hex_1.Hex.from(locktime.toHexString());
    if (locktimeHex.toString().length != 8) {
        throw new Error("Refund locktime must be a 4 bytes number");
    }
    // Bitcoin locktime is interpreted as little-endian integer so we must
    // adhere to that convention by converting the locktime accordingly.
    return locktimeHex.reverse().toString();
}
exports.calculateDepositRefundLocktime = calculateDepositRefundLocktime;
/**
 * Calculates a Bitcoin locking script hash for P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @param witness - If true, a witness script hash will be created.
 *        Otherwise, a legacy script hash will be made.
 * @returns Buffer with script hash.
 */
async function calculateDepositScriptHash(deposit, witness) {
    const script = await assembleDepositScript(deposit);
    // Parse the script from HEX string.
    const parsedScript = bcoin_1.default.Script.fromRaw(Buffer.from(script, "hex"));
    // If witness script hash should be produced, SHA256 should be used.
    // Legacy script hash needs HASH160.
    return witness ? parsedScript.sha256() : parsedScript.hash160();
}
exports.calculateDepositScriptHash = calculateDepositScriptHash;
/**
 * Calculates a Bitcoin target address for P2(W)SH deposit transaction.
 * @param deposit - Details of the deposit.
 * @param network - Network that the address should be created for.
 * @param witness - If true, a witness address will be created.
 *        Otherwise, a legacy address will be made.
 * @returns Address as string.
 */
async function calculateDepositAddress(deposit, network, witness) {
    const scriptHash = await calculateDepositScriptHash(deposit, witness);
    const address = witness
        ? bcoin_1.default.Address.fromWitnessScripthash(scriptHash)
        : bcoin_1.default.Address.fromScripthash(scriptHash);
    return address.toString((0, bitcoin_network_1.toBcoinNetwork)(network));
}
exports.calculateDepositAddress = calculateDepositAddress;
/**
 * Reveals the given deposit to the on-chain Bridge contract.
 * @param utxo - Deposit UTXO of the revealed deposit
 * @param deposit - Data of the revealed deposit
 * @param bitcoinClient - Bitcoin client used to interact with the network
 * @param bridge - Handle to the Bridge on-chain contract
 * @param vault - vault
 * @returns Transaction hash of the reveal deposit transaction as string
 * @dev The caller must ensure that the given deposit data are valid and
 *      the given deposit UTXO actually originates from a deposit transaction
 *      that matches the given deposit data.
 */
async function revealDeposit(utxo, deposit, bitcoinClient, bridge, vault) {
    const depositTx = (0, bitcoin_1.decomposeRawTransaction)(await bitcoinClient.getRawTransaction(utxo.transactionHash));
    return await bridge.revealDeposit(depositTx, utxo.outputIndex, deposit, vault);
}
exports.revealDeposit = revealDeposit;
/**
 * Gets a revealed deposit from the bridge.
 * @param utxo Deposit UTXO of the revealed deposit
 * @param bridge Handle to the Bridge on-chain contract
 * @returns Revealed deposit data.
 */
async function getRevealedDeposit(utxo, bridge) {
    return bridge.deposits(utxo.transactionHash, utxo.outputIndex);
}
exports.getRevealedDeposit = getRevealedDeposit;
/**
 * Suggests a wallet that should be used as the deposit target at the given moment.
 * @param bridge Handle to the Bridge on-chain contract.
 * @returns Compressed (33 bytes long with 02 or 03 prefix) public key of
 *          the wallet.
 */
async function suggestDepositWallet(bridge) {
    return bridge.activeWalletPublicKey();
}
exports.suggestDepositWallet = suggestDepositWallet;
//# sourceMappingURL=deposit.js.map